import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Loader from '../UI/Loader/Loader';
import { useLogin } from '../../hooks/useLogin';
import { usePasswordReset } from '../../hooks/usePasswordReset';
import LoginForm from './components/LoginForm';
import PasswordResetForm from './components/PasswordResetForm';
import OtpVerification from './components/OtpVerification';
import CreateNewPasswordForm from './components/CreateNewPasswordForm';
import './Login.css';

type ViewMode = 'login' | 'resetPassword' | 'otpVerification' | 'createNewPassword';

const Login = () => {
  const location = useLocation();
  const search = location.search;
  const epubtoken = new URLSearchParams(search).get('epubtoken');

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('login');

  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Hooks
  const { handleLogin, handleEpubLogin, isLoading: loginLoading, error: loginError } = useLogin();
  const {
    requestPasswordReset,
    verifyOtp,
    createNewPassword: createNewPasswordAPI,
    isLoading: resetLoading,
    error: resetError,
    uuid,
    email: savedEmail,
    otp: savedOtp,
    cancelPasswordReset
  } = usePasswordReset();

  // Handle ePublisher token auto-login
  useEffect(() => {
    let isMounted = true;

    const handleEpubAutoLogin = async () => {
      if (epubtoken && isMounted) {
        try {
          await handleEpubLogin({ epubtoken });
        } catch (error) {
          // Error is already handled in the hook
        }
      }
    };

    handleEpubAutoLogin();

    return () => {
      isMounted = false;
    };
  }, [epubtoken, handleEpubLogin]);

  // Login handlers
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await handleLogin({ email, password });
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleForgotPassword = () => {
    setViewMode('resetPassword');
    setResetEmail(email);
  };

  // Password reset handlers
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await requestPasswordReset({ email: resetEmail });
      setViewMode('otpVerification');
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleOtpSubmit = async () => {
    try {
      await verifyOtp({ verificationCode: otpValue, uuid });
      setViewMode('createNewPassword');
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleCreateNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createNewPasswordAPI({
        email: savedEmail,
        verificationCode: savedOtp,
        uuid,
        password: newPassword,
        confirmPassword: confirmNewPassword
      });
      // Reset and return to login
      setViewMode('login');
      setResetEmail('');
      setOtpValue('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  // Cancel handlers
  const handleCancelReset = () => {
    cancelPasswordReset();
    setViewMode('login');
    setResetEmail('');
    setOtpValue('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const isLoading = loginLoading || resetLoading;
  const currentError = viewMode === 'login' ? loginError : resetError;

  return (
    <div className="row" style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div className="col-md-6 col-lg-4">
        <div className="card card-body">
          {viewMode === 'login' && (
            <LoginForm
              email={email}
              password={password}
              error={currentError}
              isLoading={isLoading}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onSubmit={handleLoginSubmit}
              onForgotPassword={handleForgotPassword}
            />
          )}

          {viewMode === 'resetPassword' && (
            <PasswordResetForm
              email={resetEmail}
              error={currentError}
              isLoading={isLoading}
              onEmailChange={setResetEmail}
              onSubmit={handleResetPasswordSubmit}
              onCancel={handleCancelReset}
            />
          )}

          {viewMode === 'otpVerification' && (
            <OtpVerification
              otp={otpValue}
              error={currentError}
              isLoading={isLoading}
              onOtpChange={setOtpValue}
              onSubmit={handleOtpSubmit}
              onCancel={handleCancelReset}
            />
          )}

          {viewMode === 'createNewPassword' && (
            <CreateNewPasswordForm
              password={newPassword}
              confirmPassword={confirmNewPassword}
              error={currentError}
              isLoading={isLoading}
              onPasswordChange={setNewPassword}
              onConfirmPasswordChange={setConfirmNewPassword}
              onSubmit={handleCreateNewPasswordSubmit}
              onCancel={handleCancelReset}
            />
          )}

          {isLoading && <Loader />}
        </div>
      </div>
    </div>
  );
};

export default Login;
